import {ClickPlayer, ClickType} from "./click-player";
import {Settings} from "./settings";
import {ClickScheduler} from "./click-scheduler";
import {SettingsSheet} from "./settings-sheet";
import {
    ClickSoundId,
    DEFAULT_DOWNBEAT_SOUND,
    DEFAULT_SECONDARY_SOUND,
    SoundSection,
    toClickSoundId,
} from "./sounds";

/** Keys of Settings with numeric values. */
type NumericSettingKey = {
    [K in keyof Settings]: Settings[K] extends number ? K : never
}[keyof Settings];

/** Keys of Settings with boolean values. */
type BooleanSettingKey = {
    [K in keyof Settings]: Settings[K] extends boolean ? K : never
}[keyof Settings];

class Main {

    private readonly settings: Settings;
    private audio: ClickPlayer | undefined;
    private scheduler: ClickScheduler | undefined;
    private isPlaying = false;

    constructor() {
        this.settings = this.loadSettings();

        this.initialise();
    }

    /**
     * The player is created lazily, on the first user interaction that needs
     * audio (pressing play, or picking a sound in the Settings sheet), so
     * that the AudioContext is always created inside a user gesture.
     * @private
     */
    private getOrCreateAudio(): ClickPlayer {
        if (!this.audio) {
            const audio = new ClickPlayer(this.settings.downbeatSound, this.settings.secondarySound);
            this.audio = audio;

            this.scheduler = new ClickScheduler(this.settings, () => audio.currentTime);

            // rests and downbeats are already decided by the bar event stream;
            // the audio layer only maps them to a click type - no randomness here.
            this.scheduler.onsets$.subscribe(onset => {
                const clickType: ClickType = onset.isRest
                    ? 'silent'
                    : onset.isDownBeat
                        ? 'primary'
                        : 'secondary'

                audio.click(onset.timestamp, clickType);
            });
        }
        return this.audio;
    }

    private start() {
        this.isPlaying = true;
        this.getOrCreateAudio()
            .resume()
            .then(() => this.scheduler?.start());
    }

    /**
     * A sound was picked in the Settings sheet: persist it (localStorage key
     * matches the Settings key) and swap the live click sound. Previews only
     * play while the metronome is stopped - while it is running, the next
     * scheduled click already demonstrates the new sound.
     * @private
     */
    private onSoundSelected(section: SoundSection, sound: ClickSoundId) {
        const key = section === 'downbeat' ? 'downbeatSound' : 'secondarySound';
        this.settings[key] = sound;
        localStorage.setItem(key, sound);

        const audio = this.getOrCreateAudio();
        if (section === 'downbeat') {
            audio.setPrimarySound(sound);
        } else {
            audio.setSecondarySound(sound);
        }
        if (!this.isPlaying) {
            void audio.preview(sound);
        }
    }

    private stop() {
        this.isPlaying = false;
        this.scheduler?.stop();
        this.audio?.stop();
    }

    /**
     * Wire up the UI listeners
     * @private
     */
    private initialise() {

        const playCheckbox = document.getElementById('play-checkbox') as HTMLInputElement;
        const playControl = document.getElementById('play-control') as HTMLElement;

        if (playCheckbox) {
            playCheckbox.addEventListener('click', (event) => {
                const isPlaying = (event.target as HTMLInputElement).checked;
                playControl.classList.toggle('playing', isPlaying);
                playControl.setAttribute('aria-label', isPlaying ? 'Stop' : 'Play');
                if (isPlaying) {
                    this.start();
                } else {
                    this.stop();
                }
            });
        }

        this.bindNumericControl('bpm', 'bpm');
        this.makeBpmValueEditable();
        this.bindNumericControl('beatsPerBar', 'beatsPerBar');
        this.bindNumericControl('granularity', 'granularity');
        this.bindNumericControl('space', 'spaceBias');
        this.bindNumericControl('tuplets', 'tupletBias');
        this.bindBooleanControl('hasDownbeatAlways', 'hasDownbeatAlways');

        new SettingsSheet({
            getSelection: (section) => section === 'downbeat'
                ? this.settings.downbeatSound
                : this.settings.secondarySound,
            onSelect: (section, sound) => this.onSoundSelected(section, sound),
        });

        this.wireHintPopovers();
    }

    /**
     * Wire up the per-control hint toggletips: click the badge to toggle its
     * popover, click anywhere else (or press Escape) to dismiss. Only one
     * popover is open at a time.
     * @private
     */
    private wireHintPopovers() {
        const closers: (() => void)[] = [];

        document.querySelectorAll<HTMLElement>('.hint').forEach((hint) => {
            const toggle = hint.querySelector<HTMLButtonElement>('.hint-toggle');
            const popover = hint.querySelector<HTMLElement>('.hint-popover');
            if (!toggle || !popover) {
                return;
            }

            const close = () => {
                popover.hidden = true;
                toggle.setAttribute('aria-expanded', 'false');
            };
            closers.push(close);

            toggle.addEventListener('click', (event) => {
                event.stopPropagation();
                if (popover.hidden) {
                    closers.forEach(c => c());
                    popover.hidden = false;
                    toggle.setAttribute('aria-expanded', 'true');
                } else {
                    close();
                }
            });

            popover.addEventListener('click', (event) => event.stopPropagation());

            document.addEventListener('click', close);
            document.addEventListener('keydown', (event) => {
                if (event.key === 'Escape' && !popover.hidden) {
                    close();
                    toggle.focus();
                }
            });
        });
    }

    /**
     * Bind a numeric input control to a Settings key: initialise the control
     * from settings, and on change persist to localStorage and update settings.
     * The localStorage key matches the Settings key.
     * @private
     */
    private bindNumericControl(id: string, key: NumericSettingKey) {
        const control = document.getElementById(id) as HTMLInputElement;
        if (control) {
            control.addEventListener('change', (event) => {
                const value = (event.target as HTMLInputElement).valueAsNumber
                localStorage.setItem(key, '' + value);
                this.settings[key] = value;
            });

            control.valueAsNumber = this.settings[key];
            // sync the <output> displays wired up via the inline oninput handlers
            control.dispatchEvent(new Event('input'));
        }
    }

    /**
     * Make the big bpm readout editable: click (or Enter/Space while focused)
     * to swap it for a number input; Enter/blur commits, Escape cancels.
     * Committing updates the slider and fires its change event, reusing the
     * existing bindNumericControl path to persist and update settings.
     * @private
     */
    private makeBpmValueEditable() {
        const output = document.getElementById('bpm-output') as HTMLOutputElement;
        const input = document.getElementById('bpm-input') as HTMLInputElement;
        const slider = document.getElementById('bpm') as HTMLInputElement;

        if (!output || !input || !slider) {
            return;
        }

        let cancelled = false;

        const startEditing = () => {
            input.value = output.value;
            output.hidden = true;
            input.hidden = false;
            input.focus();
            input.select();
        };

        const stopEditing = () => {
            input.hidden = true;
            output.hidden = false;
        };

        const commit = () => {
            if (!isNaN(input.valueAsNumber)) {
                const min = Number(slider.min);
                const max = Number(slider.max);
                const value = Math.min(Math.max(Math.round(input.valueAsNumber), min), max);
                output.value = '' + value;
                slider.valueAsNumber = value;
                slider.dispatchEvent(new Event('change'));
            }
            stopEditing();
        };

        output.addEventListener('click', startEditing);
        output.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                startEditing();
            }
        });

        input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                commit();
            } else if (event.key === 'Escape') {
                cancelled = true;
                stopEditing();
            }
        });

        input.addEventListener('blur', () => {
            // Escape hides the input, which fires blur; don't commit that
            if (cancelled) {
                cancelled = false;
                return;
            }
            commit();
        });
    }

    /**
     * Bind a checkbox control to a Settings key: initialise the control
     * from settings, and on click persist to localStorage and update settings.
     * The localStorage key matches the Settings key.
     * @private
     */
    private bindBooleanControl(id: string, key: BooleanSettingKey) {
        const control = document.getElementById(id) as HTMLInputElement;
        if (control) {
            control.addEventListener('click', (event) => {
                const isChecked = (event.target as HTMLInputElement).checked;
                localStorage.setItem(key, JSON.stringify(isChecked));
                this.settings[key] = isChecked;
            });

            control.checked = this.settings[key];
        }
    }

    private loadSettings(): Settings {
        return {
            bpm: +(localStorage.getItem('bpm') || 80),
            beatsPerBar: +(localStorage.getItem('beatsPerBar') || 4),
            granularity: +(localStorage.getItem('granularity') || 1),
            tupletBias: +(localStorage.getItem('tupletBias') || 1),
            minTupletDepth: 1,
            spaceBias: +(localStorage.getItem('spaceBias') || 1),
            hasDownbeatAlways: (localStorage.getItem('hasDownbeatAlways') !== 'false'),
            downbeatSound: toClickSoundId(localStorage.getItem('downbeatSound'), DEFAULT_DOWNBEAT_SOUND),
            secondarySound: toClickSoundId(localStorage.getItem('secondarySound'), DEFAULT_SECONDARY_SOUND),
        };
    }

}

new Main();

// Offline support (PWA). Production builds only: the service worker is
// generated into dist/ by scripts/generate-sw.cjs, and registering it against
// the dev server would fight hot reload.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
        // indirection so Parcel doesn't try to bundle the worker as an asset -
        // it only exists in dist/ after the Workbox step
        const serviceWorkerUrl = 'service-worker.js';
        navigator.serviceWorker
            .register(serviceWorkerUrl)
            .catch((error) => console.warn('Service worker registration failed:', error));
    });
}
